import { type ReactNode } from 'react'

import { Descriptions, Space } from '@procurify/ui'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import messages from './messages'

export interface IExpenseTypeSummaryProps {
  item: ExpenseItem
}

export const ExpenseTypeSummary = ({ item }: IExpenseTypeSummaryProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()

  const getExpenseTypeDataFields = (): {
    label: string
    value: ReactNode
  }[] => {
    switch (item.expenseTypeData.type) {
      case 'AIRFARE':
        return [
          {
            label: intl.formatMessage(messages.ticketNumberLabel),
            value: item.expenseTypeData.data.ticketNumber,
          },
          {
            label: intl.formatMessage(messages.departureDateLabel),
            value: item.expenseTypeData.data.departureDate
              ? formatDate(item.expenseTypeData.data.departureDate)
              : null,
          },
          {
            label: intl.formatMessage(messages.arrivalDateLabel),
            value: item.expenseTypeData.data.arrivalDate
              ? formatDate(item.expenseTypeData.data.arrivalDate)
              : null,
          },
          {
            label: intl.formatMessage(messages.airlineLabel),
            value: item.expenseTypeData.data.airline,
          },
        ]
      case 'LABOUR':
        return [
          {
            label: intl.formatMessage(messages.hourlyRateLabel),
            value: (
              <Price
                price={item.expenseTypeData.data.hourlyRate}
                currency={item.currency.name}
              />
            ),
          },
          {
            label: intl.formatMessage(messages.hoursOfWorkLabel),
            value: item.expenseTypeData.data.hoursOfWork,
          },
        ]
      case 'MEALS':
        return [
          {
            label: intl.formatMessage(messages.tipsLabel),
            value: (
              <Price
                price={item.expenseTypeData.data.tips}
                currency={item.currency.name}
              />
            ),
          },
        ]
      case 'MILEAGE':
        return [
          {
            label: intl.formatMessage(messages.mileageRateLabel),
            value: (
              <Price
                price={item.expenseTypeData.data.mileageRate}
                currency={item.currency.name}
              />
            ),
          },
          {
            label: intl.formatMessage(messages.distanceLabel),
            value: item.expenseTypeData.data.distance.toFixed(2),
          },
          {
            label: intl.formatMessage(messages.unitOfDistanceLabel),
            value: item.expenseTypeData.data.unitOfDistance,
          },
        ]
      case 'LODGING':
      default:
        return []
    }
  }

  const fields = getExpenseTypeDataFields()

  return (
    <Space size='small' align='stretch'>
      {fields.length > 0 && (
        <Descriptions size='small' valueAlign='right' valueWidth='fill'>
          {fields.map((field) => (
            <Descriptions.Item key={field.label} label={field.label}>
              {field.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {fields.length > 0 && <Space.Divider />}

      <Descriptions size='small' valueAlign='right' valueWidth='fill'>
        <Descriptions.Item label={intl.formatMessage(messages.subtotalLabel)}>
          <Price price={item.subtotal} currency={item.currency.name} />
        </Descriptions.Item>
      </Descriptions>
    </Space>
  )
}
